import React from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useQuery } from '@tanstack/react-query';
import { translationsClient } from '../../lib/translations';

interface InstallPWAButtonProps {
  config: any;
  translations: Record<string, Record<string, string>>;
  variant?: 'navigation' | 'banner';
  platform?: 'ios' | 'android';
}

export const InstallPWAButton: React.FC<InstallPWAButtonProps> = ({
  config,
  translations: propTranslations,
  variant = 'navigation',
  platform
}) => {
  const { i18n } = useTranslation();
  const { isInstallable, isIOSSafari, promptInstall } = usePWAInstall();

  // Charger les traductions
  const { data: translations } = useQuery({
    queryKey: ['translations', i18n.language],
    queryFn: () => translationsClient.getGeneralTranslations(),
    staleTime: 5 * 60 * 1000
  });

  // Forcer le mode iOS ou Android selon le paramètre platform
  const forceIOS = platform === 'ios';
  const forceAndroid = platform === 'android';

  // Ne rien afficher si l'app n'est pas installable et qu'on n'est pas en mode test
  if (!isInstallable && !platform) {
    return null;
  }

  const getTranslatedText = (key: string): string => {
    const translation = translations?.[key] || propTranslations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const handleInstall = async () => {
    try {
      console.log('[PWA] Starting installation...');
      const installed = await promptInstall();
      if (installed) {
        console.log('[PWA] Installation successful');
      } else {
        console.log('[PWA] Installation cancelled or failed');
      }
    } catch (error) {
      console.error('[PWA] Installation error:', error);
    }
  };

  if (variant === 'banner') {
    return (
      <div className="text-center">
        <h2 
          className="text-xl font-semibold mb-2"
          style={{ color: config['dark-color'] }}
        >
          {getTranslatedText('pwa_install_title')}
        </h2>
        <p className="text-gray-600 mb-4">
          {getTranslatedText('pwa_install_description')}
        </p>
        {(isIOSSafari || forceIOS) && !forceAndroid ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <span className="text-gray-500">1.</span>
              <Download size={20} className="text-gray-500" />
              <span className="text-gray-600">{getTranslatedText('pwa_tap_share')}</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <span className="text-gray-500">2.</span>
              <span className="text-gray-600">{getTranslatedText('pwa_add_home')}</span>
            </div>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            className="flex items-center justify-center w-full px-4 py-2 rounded-lg text-white gap-2"
            style={{ 
              backgroundColor: config['major-color'],
              borderRadius: config.border_radius 
            }}
          >
            <Download size={20} />
            <span>{getTranslatedText('pwa_install_app')}</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={handleInstall}
      className="flex items-center justify-center w-full px-4 py-2 rounded-lg text-white gap-2"
      style={{ 
        backgroundColor: config['major-color'],
        borderRadius: config.border_radius 
      }}
    >
      <Download size={20} />
      <span>{getTranslatedText('pwa_install_app')}</span>
    </button>
  );
};